import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Button, Card, CardBody, CardText, CardHeader, Col, Label, Row } from "reactstrap";
import { useContext, useEffect, useState } from "react";
import ProductContext from "../../../../contexts/ProductContext";
import { AvField, AvForm } from "availity-reactstrap-validation";
import Select from "react-select";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const filterOptions = (
    candidate,
    input
) => {
    if (input) {
        const candidateInput = candidate.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");
        const normalizedInput = input.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

        return candidateInput.indexOf(normalizedInput) >= 0 || ((candidate.data.keywords || []).findIndex((keyword) => {
            const normalizedKeyword = keyword.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");

            return normalizedKeyword.indexOf(normalizedInput) >= 0
        }) >= 0);
    }

    return true;
};

const BasicInformation = ({
    t,
    onNextButtonClicked,
    onCancelButtonClicked
}) => {
    const [selectedProduct, setSelectedProduct] = useState();
    const [selectedLegalEntities, setSelectedLegalEntities] = useState([]);

    const {
        fetchingDetails,
        productData, 
        setProductData,

        productsList,
        entitiesList
    } = useContext(ProductContext);

    useEffect(() => {
        if (!fetchingDetails) {
            if (productData?.name) {
                setSelectedProduct(
                    productData.name
                );
            }

            if (productData?.legal_entity_ids) {
                setSelectedLegalEntities(
                    productData?.legal_entity_ids
                );
            }
        }
    }, [ productData, fetchingDetails ]);

    useEffect(() => {
        if (selectedProduct) {
            setProductData((currentState) => {
                return {
                    ...currentState,
                    name: selectedProduct
                }
            })
        }
    }, [ selectedProduct ]);

    const handleSubmit = (e, values) => {
        setProductData((currentState) => {
            return {...currentState, ...values}
        });

        onNextButtonClicked();
    }

    const hasEnitityFeature = isFeatureEnabledForActiveModule('entity_management');

    return (
        <Card>
            <CardHeader className="bg-white">
                <h5 className="mb-1">
                    {t('Product Information')}
                </h5>
                <p className="text-muted mb-0">
                    {t('Select the product and provide its key identifiers, such as lot number, internal ID, and description.')}
                </p>
            </CardHeader>
            <CardBody>
                <AvForm
                    className="needs-validation m-2"
                    onValidSubmit={handleSubmit}
                >
                    <Row>
                        <Col sm='12' md={hasEnitityFeature ? '4' : '6'} className="mb-3">
                            <Label>
                                {t('NACE Code')}
                            </Label>
                            <Select
                                isLoading={productsList.isFetching || fetchingDetails}
                                isDisabled={productsList.isFetching || fetchingDetails}
                                placeholder={t("NACE Code")}
                                classNamePrefix="select2-selection"
                                options={productsList.data}
                                onChange={setSelectedProduct}
                                value={selectedProduct}
                            />
                            <AvField
                                type="hidden"
                                name="name"
                                validate={{
                                    required: { value: true }
                                }}
                                errorMessage={t("This field cannot be blank")}
                                value={selectedProduct}
                            />
                        </Col>

                        <Col sm='12' md={hasEnitityFeature ? '4' : '6'} className="mb-3">
                            <Label>
                                {t('Product Name')}
                            </Label>

                            <AvField
                                type="text"
                                name="productName"
                                validate={{
                                    required: { value: false }
                                }}
                                errorMessage={t("This field cannot be blank")}
                                value={productData?.productName}
                                disabled={fetchingDetails}
                            />
                        </Col>

                        {hasEnitityFeature && (
                            <Col sm='12' md='4' className="mb-3">
                                <Label for="legal-entities">
                                    {t("Legal Entities")}
                                </Label>

                                <Select
                                    isMulti
                                    placeholder={t("Select") + "..."}
                                    classNamePrefix="select2-selection"
                                    id="legal-entities"
                                    options={entitiesList?.data}
                                    onChange={(e) => {
                                        setSelectedLegalEntities(e)
                                    }}
                                    value={selectedLegalEntities}
                                    filterOption={filterOptions}
                                    isDisabled={entitiesList.isFetching || fetchingDetails}
                                    isLoading={entitiesList.isFetching || fetchingDetails}
                                />
                                <AvField
                                    type="hidden"
                                    name="legal_entity_ids"
                                    validate={{
                                        required: { value: false }
                                    }}
                                    errorMessage={t("This field cannot be blank")}
                                    value={selectedLegalEntities}
                                />
                            </Col>
                        )}

                        <Col sm='12' md="4" className="mb-3">
                            <Label htmlFor="lotNumber">
                                {t('Lot Number')}
                            </Label>
                            <AvField
                                id="lotNumber"
                                name="lotNumber"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.lotNumber}
                                disabled={fetchingDetails}
                            />
                        </Col>

                        <Col sm='12' md="4" className="mb-3">
                            <Label htmlFor="internalProductId">
                                {t('Internal Product ID')}
                            </Label>
                            <AvField
                                id="internalProductId"
                                name="internalProductId"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.internalProductId}
                                disabled={fetchingDetails}
                            />
                        </Col>

                        <Col sm='12' md='12' className="mb-3">
                            <Label htmlFor="description">
                                {t('Description')}
                            </Label>
                            <AvField
                                id="description"
                                name="description"
                                type="textarea"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: false }
                                }}
                                value={productData?.description}
                                disabled={fetchingDetails}
                                rows="4"
                            />
                        </Col>

                        <Col sm='12' className="d-flex justify-content-between">
                            <Button
                                color="secondary"
                                type="button"
                                outline
                                onClick={onCancelButtonClicked}
                            >
                                {t('Cancel')}
                            </Button>

                            <div>
                                <Button
                                    color="primary"
                                    type="submit"
                                    disabled={fetchingDetails}
                                >
                                    {t('Next')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </AvForm>
            </CardBody>
        </Card>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(BasicInformation))
)